import * as d3 from "d3";
import { format, startOfDay } from "date-fns";
import React, { useEffect, useMemo, useRef } from "react";

import { useStyles } from "@/components/organisms/DateTimeline/DateTimeline.hooks";
import { type ChartSeriesType } from "@/components/organisms/DateTimeline/DateTimeline.types";
import { generateRoundedRectPath } from "@/components/organisms/DateTimeline/DateTimeline.utils";

interface DateTimelineBrushPropsType<T> {
  adjustedXScale: d3.ScaleBand<Date>;
  brushDateSelectionRef: React.MutableRefObject<[Date, Date] | null>;
  brushHeight: number;
  brushSpacing: number;
  brushXScale: d3.ScaleBand<Date>;
  chartContainer: d3.Selection<SVGGElement, unknown, null, undefined>;
  data: T[];
  dateKey: keyof T;
  chartGroup: d3.Selection<SVGGElement, unknown, null, undefined> | null;
  chartHeight: number;
  finalStyles: ReturnType<typeof useStyles>;
  seriesStyles: any[];
  isBrushControlled: boolean;
  isBrushVisible: boolean;
  isInitialized: React.MutableRefObject<boolean>;
  margin: { top: number; right: number; bottom: number; left: number };
  miniYScale: d3.ScaleLinear<number, number>;
  onBrushChangeRef: React.MutableRefObject<
    ((extent: [Date, Date] | null) => void) | undefined
  >;
  selectedRange: [Date, Date] | null | undefined;
  series: ChartSeriesType[];
  width: number;
  yScale: d3.ScaleLinear<number, number>;
}

export function DateTimelineBrush<T>({
  adjustedXScale,
  brushDateSelectionRef,
  brushHeight,
  brushSpacing,
  brushXScale,
  chartContainer,
  data,
  dateKey,
  chartGroup,
  chartHeight,
  finalStyles,
  seriesStyles,
  isBrushControlled,
  isBrushVisible,
  isInitialized,
  margin,
  miniYScale,
  onBrushChangeRef,
  selectedRange,
  series,
  width,
  yScale,
}: DateTimelineBrushPropsType<T>) {
  const brushRef = useRef<d3.BrushBehavior<unknown> | null>(null);
  const brushGRef = useRef<d3.Selection<
    SVGGElement,
    unknown,
    null,
    undefined
  > | null>(null);
  const isInternalBrushMove = useRef(false);
  const updateBrushSelectionUIRef = useRef<
    (selection: [number, number] | null) => void
  >(() => {});

  const chartWidth = useMemo(
    () => width - margin.left - margin.right,
    [width, margin],
  );

  useEffect(() => {
    if (
      !chartContainer ||
      !chartGroup ||
      !data.length ||
      !isBrushVisible ||
      chartWidth <= 0
    ) {
      brushGRef.current?.style("opacity", 0);
      return;
    }

    if (!brushGRef.current) {
      brushGRef.current = chartContainer
        .append("g")
        .attr("class", "brush-container");
    }

    const brushG = brushGRef.current;
    brushG
      .interrupt()
      .attr("transform", `translate(0,${chartHeight + brushSpacing})`)
      .style("opacity", 1);

    brushG.selectAll("*").remove();

    const brushPadding = finalStyles.brushStyle.padding ?? 0;
    const brushInnerWidth = chartWidth - 2 * brushPadding;

    const startSnapPoints = data.map((d) => brushXScale(d[dateKey] as Date)!);
    const endSnapPoints = data.map(
      (d) => brushXScale(d[dateKey] as Date)! + brushXScale.bandwidth(),
    );

    const snapToClosest = (points: number[], x: number): number => {
      if (!points || points.length === 0) return x;
      return points.reduce((prev, curr) =>
        Math.abs(curr - x) < Math.abs(prev - x) ? curr : prev,
      );
    };

    brushG
      .append("rect")
      .attr("x", 0)
      .attr("y", 0)
      .attr("width", chartWidth)
      .attr("height", brushHeight)
      // As a style, which reads the theme's corner variable - an ``rx``
      // attribute does not, and drew the corner square.
      .style(
        "rx",
        typeof finalStyles.brushStyle.backgroundBorderRadius === "number"
          ? `${finalStyles.brushStyle.backgroundBorderRadius}px`
          : finalStyles.brushStyle.backgroundBorderRadius,
      )
      .attr("fill", finalStyles.brushStyle.backgroundColor);

    const brushContentG = brushG
      .append("g")
      .attr("transform", `translate(${brushPadding}, 0)`);

    const brushSeriesGroup = brushContentG
      .append("g")
      .attr("class", "brush-series-group");

    const brushBarLikeSeriesIndices = new Map<number, number>();
    let currentBrushBarLikeIndex = 0;
    series.forEach((s, i) => {
      if (s.type === "bar" && !s.isExcludedFromBrush) {
        brushBarLikeSeriesIndices.set(i, currentBrushBarLikeIndex);
        currentBrushBarLikeIndex++;
      }
    });
    const numBrushBarLikeSeries = brushBarLikeSeriesIndices.size;
    const allBrushSeriesBandwidth =
      brushXScale.bandwidth() / (numBrushBarLikeSeries || 1);

    const barLikeSeriesInfo = series
      .map((s, i) => ({ s, i }))
      .filter(({ i }) => brushBarLikeSeriesIndices.has(i))
      .map(({ s, i }) => {
        const style = seriesStyles[i].brushStyle;
        const finalWidth = style.maxWidth
          ? Math.min(allBrushSeriesBandwidth, style.maxWidth)
          : allBrushSeriesBandwidth;
        return {
          seriesIndex: i,
          width: finalWidth,
        };
      });

    const totalBarsWidth = barLikeSeriesInfo.reduce(
      (acc, info) => acc + info.width,
      0,
    );
    const groupCenteringOffset = (brushXScale.bandwidth() - totalBarsWidth) / 2;

    const barLikeOffsets = new Map<number, number>();
    let currentOffset = groupCenteringOffset;
    barLikeSeriesInfo.forEach((info) => {
      barLikeOffsets.set(info.seriesIndex, currentOffset);
      currentOffset += info.width;
    });

    series.forEach((s, i) => {
      if (s.isExcludedFromBrush) return;

      if (s.type === "bar") {
        const style = seriesStyles[i];
        const precedingBarsWidth = barLikeOffsets.get(i)!;
        const finalBrushBarWidth = barLikeSeriesInfo.find(
          (info) => info.seriesIndex === i,
        )!.width;
        const stack = d3.stack().keys(s.valueKeys);
        const stackedData = stack(data as any);
        brushSeriesGroup
          .selectAll(`.mini-bar-series-${i}`)
          .data(stackedData)
          .enter()
          .append("g")
          .attr("class", `mini-bar-group mini-bar-series-${i}`)
          .attr("fill", (d, j) => style.colors?.[j] ?? "orange")
          .selectAll("path")
          .data((d) =>
            d.map((point) => ({
              ...point,
              seriesIndex: d.index,
            })),
          )
          .enter()
          .append("path")
          .attr("d", (d: any) => {
            const x = brushXScale(d.data[dateKey])! + precedingBarsWidth;
            const y = miniYScale(d[1]);
            const height = Math.max(0, miniYScale(d[0]) - miniYScale(d[1]));
            // Determine if this bar is visually top or bottom based on visible bars for this data point
            const visibleStackedData = stackedData.filter((_, idx) => {
              const dataPoint = stackedData[idx].find(
                (point: any) => point.data === d.data,
              );
              return dataPoint && dataPoint[1] - dataPoint[0] !== 0;
            });
            const isTop =
              d.seriesIndex ===
              visibleStackedData[visibleStackedData.length - 1]?.index;
            const isBottom = d.seriesIndex === visibleStackedData[0]?.index;
            return generateRoundedRectPath(
              x,
              y,
              finalBrushBarWidth,
              height,
              style.borderRadius,
              isTop,
              isBottom,
            );
          });
      } else if (s.type === "line") {
        const chartStyle = seriesStyles[i];
        const brushStyle = seriesStyles[i].brushStyle;

        const brushLine = d3
          .line<T>()
          .x(
            (d) =>
              brushXScale(d[dateKey] as Date)! + brushXScale.bandwidth() / 2,
          )
          .y((d) => miniYScale(d[s.valueKey] || 0))
          .curve(
            s.curveType === "monotone" ? d3.curveMonotoneX : d3.curveLinear,
          );

        const chartLine = d3
          .line<T>()
          .x(
            (d) =>
              brushXScale(d[dateKey] as Date)! + brushXScale.bandwidth() / 2,
          )
          .y((d) => yScale(d[s.valueKey] || 0))
          .curve(
            s.curveType === "monotone" ? d3.curveMonotoneX : d3.curveLinear,
          );

        // Chart
        chartGroup
          .selectAll(`.line-series-${i}`)
          .data([data])
          .join("path")
          .attr("class", `line-series-${i}`)
          .attr("fill", "none")
          .attr("stroke", chartStyle.color)
          .attr("stroke-width", chartStyle.strokeWidth)
          .attr("d", chartLine);

        const clipId = `line-series-clip-${i}`;
        chartGroup
          .append("clipPath")
          .attr("id", clipId)
          .append("rect")
          .attr("y", 0)
          .attr("height", chartHeight);

        chartGroup
          .selectAll(`.line-series-selected-${i}`)
          .data([data])
          .join("path")
          .attr("class", `line-series-selected-${i}`)
          .attr("fill", "none")
          .attr("stroke", chartStyle.selectedColor)
          .attr("stroke-width", chartStyle.strokeWidth)
          .attr("d", chartLine)
          .attr("clip-path", `url(#${clipId})`);

        // Brush
        brushSeriesGroup
          .selectAll(`.mini-line-series-${i}`)
          .data([data])
          .join("path")
          .attr("class", `mini-line-series-${i}`)
          .attr("fill", "none")
          .attr("stroke", brushStyle.color)
          .attr("stroke-width", brushStyle.strokeWidth)
          .attr("d", brushLine);

        const miniClipId = `mini-line-series-clip-${i}`;
        brushContentG
          .append("clipPath")
          .attr("id", miniClipId)
          .append("rect")
          .attr("y", 0)
          .attr("height", brushHeight);

        brushSeriesGroup
          .selectAll(`.mini-line-series-selected-${i}`)
          .data([data])
          .join("path")
          .attr("class", `mini-line-series-selected-${i}`)
          .attr("fill", "none")
          .attr("stroke", brushStyle.selectedColor)
          .attr("stroke-width", brushStyle.strokeWidth)
          .attr("d", brushLine)
          .attr("clip-path", `url(#${miniClipId})`);
      }
    });

    const brushGroup = brushContentG.append("g").attr("class", "brush");
    const selectionHeight = brushHeight * 0.9;
    const selectionY = (brushHeight - selectionHeight) / 2;

    updateBrushSelectionUIRef.current = (
      selection: [number, number] | null,
    ) => {
      brushG.selectAll(".brush-label").remove();
      brushGroup.selectAll(".custom-handle").remove();

      let startDate, endDate;
      if (selection) {
        const [x0, x1] = selection;
        const selectedBars = data.filter((d) => {
          const barStart = brushXScale(d[dateKey] as Date)!;
          const barEnd = barStart + brushXScale.bandwidth();
          return x0 < barEnd && x1 > barStart;
        });
        if (selectedBars.length > 0) {
          startDate = selectedBars[0][dateKey] as Date;
          endDate = selectedBars[selectedBars.length - 1][dateKey] as Date;
        }
      }

      const isAnyDateSelected = !!(startDate && endDate);
      const isSelectedDate = (d: T) => {
        if (!isAnyDateSelected) return true;
        return (
          startOfDay(d[dateKey] as Date) >= startOfDay(startDate!) &&
          startOfDay(d[dateKey] as Date) <= startOfDay(endDate!)
        );
      };

      series.forEach((s, i) => {
        if (s.type === "bar") {
          if (!s.isExcludedFromChart) {
            const style = seriesStyles[i];
            chartGroup
              .selectAll<SVGGElement, any>(`.bar-series-${i}`)
              .each(function (d, seriesIndex) {
                d3.select(this)
                  .selectAll<SVGPathElement, any>("path")
                  .attr("fill", (rectData) => {
                    const colors = isSelectedDate(rectData.data)
                      ? style.selectedColors
                      : style.colors;
                    return colors?.[seriesIndex] ?? "orange";
                  });
              });
          }
          if (!s.isExcludedFromBrush) {
            const brushStyle = seriesStyles[i].brushStyle;
            brushContentG
              .selectAll(`.mini-bar-series-${i}`)
              .each(function (d, seriesIndex) {
                d3.select(this)
                  .selectAll<SVGPathElement, any>("path")
                  .attr("fill", (rectData) => {
                    const colors = isSelectedDate(rectData.data)
                      ? brushStyle.selectedColors
                      : brushStyle.colors;
                    return colors?.[seriesIndex] ?? "orange";
                  });
              });
          }
        } else if (s.type === "line") {
          // Keeps the selected area update to date as the user drags the brush
          if (!s.isExcludedFromChart) {
            const style = seriesStyles[i];

            chartGroup
              .selectAll(`.line-series-${i}`)
              .attr(
                "stroke",
                isAnyDateSelected ? style.color! : style.selectedColor!,
              );

            const clipId = `line-series-clip-${i}`;
            const clipPathRect = chartGroup.select(`#${clipId} rect`);
            const selectedClipPathRect = chartGroup.select(
              `.line-series-selected-${i}`,
            );

            if (selection) {
              const [x0, x1] = selection;
              clipPathRect.attr("x", x0).attr("width", x1 - x0);
              selectedClipPathRect.attr("x", x0).attr("width", x1 - x0);
            } else {
              clipPathRect.attr("width", 0);
              selectedClipPathRect.attr("x", 0).attr("width", 0);
            }
          }
          if (!s.isExcludedFromBrush) {
            const brushStyle = seriesStyles[i].brushStyle;
            brushContentG
              .selectAll(`.mini-line-series-${i}`)
              .attr(
                "stroke",
                isAnyDateSelected
                  ? brushStyle.color!
                  : brushStyle.selectedColor!,
              );

            const clipId = `mini-line-series-clip-${i}`;
            const clipPathRect = brushContentG.select(`#${clipId} rect`);
            const selectedClipPathRect = brushContentG.select(
              `.mini-line-series-selected-${i}`,
            );

            if (selection) {
              const [x0, x1] = selection;
              clipPathRect.attr("x", x0).attr("width", x1 - x0);
              selectedClipPathRect.attr("x", x0).attr("width", x1 - x0);
            } else {
              clipPathRect.attr("width", 0);
              selectedClipPathRect.attr("x", 0).attr("width", 0);
            }
          }
        }
      });

      if (selection && startDate && endDate) {
        const [x0, x1] = selection;
        brushG
          .append("text")
          .attr("class", "brush-label")
          .attr("x", x0)
          .attr("y", -2)
          .attr("text-anchor", "middle")
          .attr("fill", finalStyles.brushStyle.handleColor)
          .attr("font-size", "10px")
          .attr("font-weight", "bold")
          .text(format(startDate, "MMM dd"));
        brushG
          .append("text")
          .attr("class", "brush-label")
          .attr("x", x1)
          .attr("y", -2)
          .attr("text-anchor", "middle")
          .attr("fill", finalStyles.brushStyle.handleColor)
          .attr("font-size", "10px")
          .attr("font-weight", "bold")
          .text(format(endDate, "MMM dd"));
        brushGroup
          .select(".selection")
          .attr("rx", finalStyles.brushStyle.selectionBorderRadius);
        const handleHeight = selectionHeight * 0.7;
        const handleY = selectionY + (selectionHeight - handleHeight) / 2;
        brushGroup
          .append("rect")
          .attr("class", "custom-handle")
          .attr("x", x0 - 1)
          .attr("y", handleY)
          .attr("width", 3)
          .attr("height", handleHeight)
          .attr("rx", finalStyles.brushStyle.handleBorderRadius)
          .attr("fill", finalStyles.brushStyle.handleColor)
          .style("pointer-events", "none");
        brushGroup
          .append("rect")
          .attr("class", "custom-handle")
          .attr("x", x1 - 2)
          .attr("y", handleY)
          .attr("width", 3)
          .attr("height", handleHeight)
          .attr("rx", finalStyles.brushStyle.handleBorderRadius)
          .attr("fill", finalStyles.brushStyle.handleColor)
          .style("pointer-events", "none");
        brushGroup
          .selectAll(".handle--w, .handle--e")
          .style("cursor", "ew-resize");
      }
    };

    const handleStart = (event: d3.D3BrushEvent<unknown>) => {
      if (!event.sourceEvent) return;
      isInternalBrushMove.current = true;
      const { mode } = event;
      const brushElements = brushGroup.selectAll(
        ".selection, .handle--w, .handle--e, .overlay",
      );
      if (mode === "drag") {
        brushElements.classed("is-moving", true);
      } else if (mode === "handle") {
        brushElements.classed("is-resizing", true);
      }
    };

    const handleBrush = (event: d3.D3BrushEvent<unknown>) => {
      const selection = event.selection as [number, number] | null;

      if (event.sourceEvent && selection) {
        let [x0, x1] = selection;
        const { mode } = event;

        if (mode === "drag") {
          const newX0 = snapToClosest(startSnapPoints, x0);
          const width = x1 - x0;
          x0 = newX0;
          x1 = newX0 + width;
        } else {
          x0 = snapToClosest(startSnapPoints, x0);
          x1 = snapToClosest(endSnapPoints, x1);
        }

        if (x0 > x1) {
          [x0, x1] = [x1, x0];
        }

        const snappedSelection: [number, number] = [x0, x1];
        updateBrushSelectionUIRef.current(snappedSelection);

        if (
          selection[0] !== snappedSelection[0] ||
          selection[1] !== snappedSelection[1]
        ) {
          (event.target as any).move(brushGroup, snappedSelection);
        }
      } else {
        updateBrushSelectionUIRef.current(selection);
      }
    };

    const handleEnd = (event: d3.D3BrushEvent<unknown>) => {
      isInternalBrushMove.current = false;
      if (!event.sourceEvent) return;

      brushGroup
        .selectAll(".selection, .handle--w, .handle--e, .overlay")
        .classed("is-moving", false)
        .classed("is-resizing", false);

      let newDateRange: [Date, Date] | null = null;
      if (event.selection) {
        const [x0, x1] = event.selection as [number, number];
        const selectedBars = data.filter((d) => {
          const barStart = brushXScale(d[dateKey] as Date)!;
          const barEnd = barStart + brushXScale.bandwidth();
          return x0 < barEnd && x1 > barStart;
        });
        if (selectedBars.length > 0) {
          newDateRange = [
            selectedBars[0][dateKey] as Date,
            selectedBars[selectedBars.length - 1][dateKey] as Date,
          ];
        }
      }

      const oldStartTime = (
        isBrushControlled ? selectedRange : brushDateSelectionRef.current
      )?.[0]?.getTime();
      const oldEndTime = (
        isBrushControlled ? selectedRange : brushDateSelectionRef.current
      )?.[1]?.getTime();
      const newStartTime = newDateRange?.[0]?.getTime();
      const newEndTime = newDateRange?.[1]?.getTime();

      if (oldStartTime !== newStartTime || oldEndTime !== newEndTime) {
        onBrushChangeRef.current?.(newDateRange);
      }
      if (!isBrushControlled) {
        brushDateSelectionRef.current = newDateRange;
      }
    };

    brushRef.current = d3
      .brushX()
      .extent([
        [0, selectionY],
        [brushInnerWidth, selectionY + selectionHeight],
      ])
      .on("start", handleStart)
      .on("brush", handleBrush)
      .on("end", handleEnd);

    brushGroup.call(brushRef.current);

    const rangeToApply = isBrushControlled
      ? selectedRange
      : brushDateSelectionRef.current;

    if (rangeToApply) {
      const pixelRange = [
        brushXScale(startOfDay(rangeToApply[0]))!,
        brushXScale(startOfDay(rangeToApply[1]))! + brushXScale.bandwidth(),
      ] as [number, number];
      brushRef.current.move(brushGroup, pixelRange);
      updateBrushSelectionUIRef.current(pixelRange);
    } else {
      updateBrushSelectionUIRef.current(null);
    }

    chartGroup
      .selectAll(".overlay")
      .style("fill", "transparent")
      .style("opacity", 0);

    const miniAxis = d3
      .axisBottom(brushXScale)
      .tickFormat((d) => format(d as Date, "MM/dd"));
    const maxMiniTicks = Math.max(1, Math.floor(brushInnerWidth / 60));

    if (data.length > maxMiniTicks) {
      const stride = Math.ceil(data.length / maxMiniTicks);
      const miniTickValues = data
        .filter((_d, i) => i % stride === 0)
        .map((d) => d[dateKey] as Date);
      miniAxis.tickValues(miniTickValues);
    } else {
      miniAxis.tickValues(data.map((d) => d[dateKey] as Date));
    }

    const miniAxisGroup = brushContentG
      .append("g")
      .attr("transform", `translate(0,${brushHeight})`)
      .call(miniAxis);
    miniAxisGroup.select(".domain").remove();

    isInitialized.current = true;
  }, [
    chartContainer,
    chartGroup,
    data,
    isBrushVisible,
    finalStyles,
    brushSpacing,
    chartHeight,
    series,
    adjustedXScale,
    yScale,
    brushXScale,
    miniYScale,
    dateKey,
    seriesStyles,
    chartWidth,
  ]);

  useEffect(() => {
    const brushG = brushGRef.current;
    if (!brushG) return;

    if (isBrushControlled) {
      brushG.selectAll(".brush").style("pointer-events", "none");
    } else {
      brushG.selectAll(".brush").style("pointer-events", "all");
    }
  }, [isBrushControlled]);

  useEffect(() => {
    if (
      !isBrushControlled ||
      !brushRef.current ||
      !brushGRef.current ||
      !data.length ||
      isInternalBrushMove.current
    )
      return;

    const brushNode = brushGRef.current.select(".brush").node() as SVGGElement;
    if (!brushNode) return;

    const currentSelection = d3.brushSelection(brushNode);
    const pixelRange = selectedRange
      ? ([
          brushXScale(startOfDay(selectedRange[0]))!,
          brushXScale(startOfDay(selectedRange[1]))! + brushXScale.bandwidth(),
        ] as [number, number])
      : null;

    const hasChanged =
      JSON.stringify(currentSelection) !== JSON.stringify(pixelRange);

    if (hasChanged) {
      d3.select(brushNode).call(
        brushRef.current.move.bind(brushRef.current),
        pixelRange,
      );
      updateBrushSelectionUIRef.current(pixelRange);
    }
  }, [isBrushControlled, selectedRange, brushXScale, data, dateKey]);

  return null;
}
